import { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { BranchRouter } from "./branchRouter";
import { BranchSnapshotRegenerator } from "./branchSnapshotRegenerator";
import { DemoBranchStore } from "./DemoBranchStore";

/**
 * Hook to extract brandId and branchId from the current route
 * Pattern: /branches/wizard/{branchId}?brandId={brandId}
 * - branchId is optional in path (if exists = update, if not = create)
 * - brandId is optional query parameter (if exists = fixed, if not = selectable)
 */
export function useBranchContext() {
  const params = useParams<{
    branchId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  return useMemo(() => {
    const branchId = params.branchId;
    const brandId = searchParams.get("brandId") || undefined;

    return {
      brandId,
      branchId,
      hasFixedBrandId: !!brandId, // If brandId is in query params, it's fixed
    };
  }, [params.branchId, searchParams, location.pathname]);
}

/**
 * Hook to get snapshot from location state
 */
export function useBranchSnapshot() {
  const location = useLocation();
  return useMemo(() => {
    return BranchSnapshotRegenerator.restoreSnapshot(location.state);
  }, [location.state]);
}

/**
 * Hook to create DemoBranchStore with snapshot
 * Store should only be recreated when branchId or brandId changes, not on step changes
 */
export function useDemoBranchStore(
  brandId: string | undefined,
  branchId: string | undefined,
  hasFixedBrandId: boolean
) {
  return useMemo(() => {
    return new DemoBranchStore(brandId, branchId, hasFixedBrandId);
  }, []);
}

/**
 * Hook to create BranchRouter with store
 * Router should only be recreated when store or stepPaths structure changes,
 * not on every pathname change (which happens on step navigation)
 */
export function useBranchRouter(
  branchStore: DemoBranchStore,
  initialPathname: string,
  stepPaths: string[]
) {
  const navigate = useNavigate();

  // Create a stable reference to stepPaths by stringifying it
  // This prevents recreation when stepPaths array reference changes but content is same
  const stepPathsKey = stepPaths.join("|");

  return useMemo(() => {
    return new BranchRouter(navigate, branchStore, initialPathname, stepPaths);
    // Only recreate when store or stepPaths content changes, not on every pathname change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, branchStore, stepPathsKey]);
}
